import { useState } from 'react';
import { useNetworkUIStore } from '../store/networkUIStore';
import { useTabStore } from '../store/tabStore';
import { useZoneStore } from '../store/zoneStore';
import { switchZone } from '../services/zoneSwitchService';
import { ElementFilter } from './sidebar/ElementFilter';
import { useThemeStore } from '../store/themeStore';
import { CreateZoneDialog } from './zones/CreateZoneDialog';
import {
  useNetzanschlussEnabled,
  useReadOnlyUi,
  useSchalthistorieEnabled,
  useStresstestEnabled,
} from '../store/featureStore';

export const ControlSidebar = () => {
  const theme = useThemeStore(state => state.theme);
  // Read-only deployments (archive/demo): no zone creation, no planning/control navigation.
  const readOnly = useReadOnlyUi();
  // Field selectors only: this sidebar renders the whole zone list (148 zones on VER) and a
  // whole-store subscription re-rendered it on every 10 s power update of the map.
  const openTab = useTabStore(state => state.openTab);
  const activeTabType = useTabStore(state => state.tabs.find(tab => tab.id === state.activeTabId)?.type || 'map');

  // Zone management — per-field selectors so this component only re-renders
  // when these fields change, not on every store write
  const zones = useZoneStore(s => s.zones);
  const activeZoneId = useZoneStore(s => s.activeZoneId);
  const isZoneLoading = useZoneStore(s => s.isLoading);
  const [isCreateZoneOpen, setIsCreateZoneOpen] = useState(false);

  // Load flow store

  // Optional-module gating (deployment-level, loaded once in App.tsx)
  const netzanschlussEnabled = useNetzanschlussEnabled();
  const stresstestEnabled = useStresstestEnabled();
  const schalthistorieEnabled = useSchalthistorieEnabled();

  const handleZoneChange = async (zoneId: string) => {
    if (!zoneId) return; // NO FALLBACK - don't switch to empty zone
    await switchZone(zoneId);
  };

  const isCollapsed = useNetworkUIStore(state => state.isControlSidebarCollapsed);

  return (
    <div className={`h-full bg-white dark:bg-primary-800 border-r border-gray-300 dark:border-primary-500 shadow-lg flex flex-col transition-all duration-300 ${isCollapsed ? 'w-12' : 'w-64'}`} id="control-sidebar">
      {/* Spacer for logo overlay */}
      <div className="min-h-[56px] shrink-0" />
      <div className={`flex-1 overflow-y-auto p-3 ${isCollapsed ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}>
        {/* Zone Management Section - CRITICAL: All actions are zone-scoped */}
        <div className="mb-5">
          <h2 className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-primary-400 font-bold mb-3 px-1">ZONE</h2>
          <div className="space-y-2">
            {/* Zone Selector */}
            <div className="relative">
              <select
                value={activeZoneId || ''}
                onChange={(e) => handleZoneChange(e.target.value)}
                disabled={isZoneLoading || zones.length === 0}
                className="w-full px-3 py-2 bg-white dark:bg-primary-700 border border-gray-300 dark:border-primary-500 rounded-lg text-sm font-medium text-gray-800 dark:text-primary-100 focus:outline-hidden focus:ring-2 focus:ring-accent-primary appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              >
                {zones.length === 0 ? (
                  <option value="">Keine Zonen verfügbar</option>
                ) : (
                  <>
                    {/* Zoneless state placeholder - only shown when no zone selected */}
                    {!activeZoneId && (
                      <option value="" disabled>
                        -- Wähle eine Zone --
                      </option>
                    )}
                    {zones.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {/* Dropdown Arrow */}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-primary-300">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                </svg>
              </div>
            </div>

            {/* Create Zone Button */}
            {!readOnly && (
              <button
                onClick={() => setIsCreateZoneOpen(true)}
                className="w-full px-3 py-2 bg-accent-primary hover:bg-accent-primary-hover text-white border border-accent-primary rounded-lg text-sm font-medium transition-all duration-200 shadow-sm focus:outline-hidden focus:ring-2 focus:ring-accent-primary flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Neue Zone</span>
              </button>
            )}
          </div>
        </div>

        {/* Main navigation section */}
        <div className="mb-5">
          <h2 className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-primary-400 font-bold mb-3 px-1">ANSICHT</h2>
          <div className="space-y-1.5">
            <button
              onClick={() => openTab(activeTabType === 'map' ? 'grid' : 'map')}
              className="w-full px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-primary-200 dark:hover:bg-primary-700 dark:hover:text-white transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-primary flex items-center space-x-3"
            >
              {activeTabType === 'map' ? (
                <>
                  <svg className="w-5 h-5 shrink-0 text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  <span className="flex-1 text-left">Tabellenansicht</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 shrink-0 text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <span className="flex-1 text-left">Kartenansicht</span>
                </>
              )}
            </button>

            <button
              onClick={() => openTab('documentation')}
              className="w-full px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-primary-200 dark:hover:bg-primary-700 dark:hover:text-white transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-primary flex items-center space-x-3"
            >
              <svg className="w-5 h-5 shrink-0 text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span className="flex-1 text-left">Hilfe & Einstieg</span>
            </button>

            {/* Planning / control features make no sense on an archive-fed
                read-only instance: no live grid to stress, no §14a switching,
                no connection requests. Hidden there (UI-Audit follow-up).
                Stressszenarios and Schalthistorie can additionally be switched
                off per instance (STRESSTEST_ENABLED / SCHALTHISTORIE_ENABLED)
                where the module is outside the pilot scope. */}
            {!readOnly && stresstestEnabled && (
            <button
              onClick={() => openTab('stress')}
              className="w-full px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-primary-200 dark:hover:bg-primary-700 dark:hover:text-white transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-primary flex items-center space-x-3"
            >
              <svg className="w-5 h-5 shrink-0 text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span className="flex-1 text-left">Stressszenarios</span>
            </button>
            )}

            {!readOnly && schalthistorieEnabled && (
            <button
              onClick={() => openTab('switching-history')}
              className="w-full px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-primary-200 dark:hover:bg-primary-700 dark:hover:text-white transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-primary flex items-center space-x-3"
            >
              <svg className="w-5 h-5 shrink-0 text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="flex-1 text-left">Schalthistorie</span>
            </button>
            )}

            {netzanschlussEnabled && !readOnly && (
              <button
                onClick={() => openTab('netzanschluss')}
                className="w-full px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-primary-200 dark:hover:bg-primary-700 dark:hover:text-white transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-primary flex items-center space-x-3"
              >
                <svg className="w-5 h-5 shrink-0 text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="flex-1 text-left">Netzanschlussprüfung</span>
              </button>
            )}

            <button
              onClick={() => openTab('settings')}
              className="w-full px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-primary-200 dark:hover:bg-primary-700 dark:hover:text-white transition-colors duration-150 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-primary flex items-center space-x-3"
            >
              <svg className="w-5 h-5 shrink-0 text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="flex-1 text-left">Einstellungen</span>
            </button>
          </div>
        </div>

        {/* Filter section */}
        <div className="mb-4">
          <h2 className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-primary-400 font-bold mb-2 px-1">Filter</h2>
          <ElementFilter />
        </div>

      </div>

      {/* Logos at the bottom */}
      <div className={`mt-auto p-4 ${isCollapsed ? 'hidden' : 'block'}`}>
        <div className="flex items-center justify-center gap-4">
          <img src={theme === 'dark' ? '/assets/logo-dark.svg' : '/assets/meterpan.png'} alt="Meterpan Logo" className="h-8 max-w-[45%] object-contain" />
          <img src="/assets/MAAS_rot.png" alt="MaaS Logo" className="h-8 max-w-[45%] object-contain" />
        </div>
        {__APP_VERSION__ && (
          <div className="text-center mt-2">
            <span className="text-xs text-gray-400 dark:text-primary-500">v{__APP_VERSION__}</span>
          </div>
        )}
      </div>

      {/* Create Zone Dialog */}
      <CreateZoneDialog
        isOpen={isCreateZoneOpen}
        onClose={() => setIsCreateZoneOpen(false)}
        onCreated={async (newZone) => {
          setIsCreateZoneOpen(false);
          // Automatically switch to the new zone
          await handleZoneChange(newZone.id);
        }}
      />

    </div>
  );
};
